// Import user role types from dedicated file
import { UserRoleType } from './userRoles';

// Tenant status types
export const TenantStatus = {
    ACTIVE: 'ACTIVE',
    INACTIVE: 'INACTIVE',
} as const;

export type TenantStatus = (typeof TenantStatus)[keyof typeof TenantStatus];

export type TelephonyProvider = 'twilio' | 'telnyx';

// Twilio Credentials type
export interface TwilioCredentials {
    twilioAccountSID: string;
    twilioAPIKey: string;
    twilioAPIKeySecret: string;
}

// LawRuler Credentials type
export interface LawRulerCredentials {
    portal?: string;
    clientId?: string;
    clientSecret?: string;
    username?: string;
    password?: string;
}

// CRM Credentials type
export interface CrmCredentials {
    lawruler?: LawRulerCredentials;
}

// Tenant configuration type
export interface TenantConfig {
    status?: TenantStatus;
    twilioCredentials?: TwilioCredentials;
    dashboardData?: {
        infos?: string[];
        charts?: string[];
        metrics?: string[];
    };
    crmCredentials?: CrmCredentials;
    businessHours?: {
        startTime?: string;
        endTime?: string;
    };
}

// Tenant edges type (relations to other entities)
export interface TenantEdges {
    users?: User[];
    // Add other relations as needed
}

// Workflow type
export interface Workflow {
    id: string;
    title: string;
}

// Lightweight workflow summary returned by assignment endpoints
export interface WorkflowSummary {
    id: string;
    title: string;
}

// Main Tenant type
export interface Tenant {
    id: string; // UUID as string in frontend
    name: string;
    outboundTrunkSid?: string;
    phoneNumber?: string;
    transferNumber?: string;
    created_at: string; // ISO date string
    deleted_at?: string | null; // Nullable for soft deletes
    updated_at: string;
    config?: TenantConfig;
    edges?: TenantEdges;
    users?: User[];
    workflows?: Workflow[];
}

// User type
export interface User {
    id: string; // UUID as string in frontend
    first_name: string;
    last_name: string;
    email: string;
    password?: string; // Optional, might not be returned from API
    roles: UserRoleType[];
    tenant_id?: string;
    created_at: string;
    deleted_at?: string | null;
    updated_at: string;
    cognito_id?: string;
    edges?: Record<string, unknown>;
    workflow_assignments?: WorkflowSummary[];
    invite_status?: 'pending' | 'active';
}

// API Response types
export interface GetAccessManagerDataResponse {
    // The company's people with their workflow assignments (Manager Command
    // Center's agent lists).
    users?: User[];
    tenants?: Tenant[];
}

// Active Account type
export interface ActiveAccount {
    sid: string;
    friendlyName: string;
}

// Outbound Trunk Info type
export interface OutboundTrunkInfo {
    trunk_id: string;
    name: string;
    phone_numbers: string[];
    address: string;
}

// Request types
export interface CreateTenantRequest {
    name: string;
    provider: TelephonyProvider;
    twilioSubAccountSID?: string;
    outboundTrunkSid?: string;
    phoneNumber?: string;
    transferNumber?: string;
    tenantConfig: TenantConfig;
}

// Delete response type
export interface DeleteResponse {
    message: string;
}

// Masked tenant config types (for the read-only config viewer)

export interface MaskedTwilioCredentials {
    twilioAccountSID?: string;
    twilioAPIKey?: string;
    twilioAPIKeySecret?: string;
    twilioAuthToken?: string;
    businessProfileSid?: string;
    cnamTrustProductSid?: string;
    shakenStirTrustProductSid?: string;
    twilioMessagingServiceSid?: string;
    twilioTrunkSids?: Record<string, string>;
    workflowInboundLivekitTrunks?: Record<string, string>;
}

export interface MaskedLawRulerCredentials {
    portal?: string;
    clientId?: string;
    clientSecret?: string;
    username?: string;
    password?: string;
}

export interface MaskedCrmCredentials {
    lawruler?: MaskedLawRulerCredentials;
}

export interface BusinessHoursConfig {
    startTime?: string;
    endTime?: string;
    timezone?: string;
}

export interface OperatingHoursConfig {
    startTime?: string;
    endTime?: string;
    timezone?: string;
}

export interface StaleCallsConfig {
    eligibleResetMinutes?: number;
    inProgressMinutes?: number;
    transferSeconds?: number;
    dialingMinutes?: number;
    ringingMinutes?: number;
    manualPendingMinutes?: number;
    manualAgentConnectedMinutes?: number;
    manualInProgressMinutes?: number;
    humanOnCallMinutes?: number;
}

export interface CallQueueConfig {
    allowCallsWithoutOnlineAgents?: boolean;
}

export interface MaskedTenantConfig {
    status?: string;
    twilioCredentials?: MaskedTwilioCredentials;
    dashboardData?: Record<string, unknown>;
    crmCredentials?: MaskedCrmCredentials;
    businessHours?: BusinessHoursConfig;
    operatingHours?: OperatingHoursConfig;
    staleCallsConfig?: StaleCallsConfig;
    callQueueConfig?: CallQueueConfig;
    [key: string]: unknown;
}

export interface MaskedTenantConfigResponse {
    id: string;
    name: string;
    created_at: string;
    updated_at: string;
    config: MaskedTenantConfig;
}

// ---- User management by product (/api/v1/members) ----
// One API behind the user screens of every product. Who sees and edits whom is
// decided by the backend per product; the screens render what it answers
// (`manageable`) and never hard-code a role list.

export type Product = 'platform' | 'onboarding' | 'voice_recorder';

// Role keys per product: { platform: ['ADMIN'], voice_recorder: ['MANAGER'] }.
export type ProductRoles = Partial<Record<Product, string[]>>;

export interface Member {
    // The users row id — the user id other services know.
    id: string;
    email: string;
    first_name: string;
    last_name: string;
    invite_status: 'pending' | 'active';
    created_at: string;
    // The company's owner.
    owner: boolean;
    // "SUPER_ADMIN" for SLAI staff.
    staff?: string;
    // Roles in the products the caller manages — only those.
    products: ProductRoles;
}

export interface MembersResponse {
    account_id: string;
    // Per product the caller manages here, the roles they may hand out.
    manageable: ProductRoles;
    members: Member[];
}

export interface CreateMemberRequest {
    email: string;
    first_name: string;
    last_name: string;
    // Absent for an invite: Cognito mails a temporary password.
    password?: string;
    products: ProductRoles;
}

// Listed products are replaced ([] takes the person out of one); the others
// are left as they are. The email cannot change.
export interface UpdateMemberRequest {
    first_name: string;
    last_name: string;
    password?: string;
    products: ProductRoles;
}

// Deleted outright, or only taken out of the caller's products.
export interface DeleteMemberResponse {
    deleted: boolean;
    removed_from?: Product[];
}

// ---- Companies and their products (/api/v1/accounts, SLAI staff) ----

export interface CompanyProduct {
    // The entitlement: ACTIVE, ONBOARDING, PAST_DUE, SUSPENDED, OFF.
    status: string;
    // The product lets the company in with this status.
    enabled: boolean;
    // Staff switch it on and off here. Every product is its own switch
    // (Platform, Agent Builder, Voice Recorder): none follows another or the
    // company's lifecycle.
    switchable: boolean;
}

export interface Company {
    id: string;
    name: string;
    products: Partial<Record<Product, CompanyProduct>>;
}

export interface CompaniesResponse {
    companies: Company[];
}
